import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F1120lSchLService } from './f1120l-sch-l.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-f1120l-sch-l',
    templateUrl: './f1120l-sch-l.component.html',
    styleUrls: ['./f1120l-sch-l.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120lSchLComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  // Modal properties
  modalTitle = "Schedule L Total Assets";
  showFormLoader = true;
  crudLoading = false;
  
  // Tab management
  activeTab = 1;
  tabs = [
    { id: 1, name: 'Form Entry' },
    { id: 2, name: 'Form View' },
    { id: 3, name: 'Audit' }
  ];

  // Entity data properties
  entityList: any[] = [];
  leId: any;
  cdr_no: any;
  leType: any;

  // Form data
  scheduleLFormView: any = { rows: [] };
  formListOriginal: any;
  formData: any = {
    "addrows": [],
    "editrows": [],
    "removerows": []
  };

  // Batch data for Audit tab
  BatchList: any[] = [];
  BatchListData: any[] = [];
  batchLoader = false;
  showBatchData = false;
  showRequestLoader = true;
  prev_index: any = null;


  // Context from workspace services
  rowData: any = {};
  filterParams: any = {};
  baseUrls: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f1120lSchLService: F1120lSchLService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.globalService.filterParams = this.filterParams;
    this.cdr.markForCheck();
  }

  init() {
    try {
      // CRITICAL: Wait for globalService and GENERAL_CONFIG to be ready
      if (!this.globalService || !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
        return;
      }

      // Only proceed if we have row data and not already initialized
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        // Setup basic entity properties
        this.leId = this.rowData.LEID;
        this.cdr_no = this.rowData.MARS;
        this.leType = this.rowData.LE_TYPE;
        
        // Setup entity list as array for ngFor
        console.log('[DEBUG] rowData for entityList:', this.rowData);
        console.log('[DEBUG] ME value:', this.rowData.ME);
        this.entityList = [
          { key: "Tax Year", value: this.rowData.TAX_YEAR },
          { key: "LEID", value: this.rowData.LEID },
          { key: "LE Name", value: this.rowData.LE_NAME },
          { key: "LE TYPE", value: this.rowData.LE_TYPE },
          { key: "ME", value: this.rowData.ME }
        ];

        // Load initial data
        console.log('[DEBUG] Calling fetchSchLrecords from init()');
        this.fetchSchLrecords();
        this.fetchSchLimports();
      }
    } catch (e) {
      console.error('Error in F1120L-SCH-L init:', e);
      this.showFormLoader = false;
    }
  }


  fetchSchLrecords() {
    const params = {
      "action_id": 30177,
      "tax_year": this.rowData.TAX_YEAR,
      "form_no": this.rowData.FORM_NO,
      "schd_name": this.rowData.SCHD_NAME,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadPivotFormJson',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.processFormData(data);
        this.showFormLoader = false;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading form data:', error);
        this.showFormLoader = false;
        this._snackBar.open('Error loading form data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  processFormData(data: any) {
    if (data && data.irsForm && data.irsForm.formItemList) {
      this.scheduleLFormView.rows = [];

      for (let i = 0; i < data.irsForm.formItemList.length; i++) {
        const saveParamDtls = data.irsForm.formItemList[i].dataRowList[0].rowDimensions.object_id.split('~');
        const tempDataObj: any = {
          saveParams: saveParamDtls
        };

        const dataCellList = data.irsForm.formItemList[i].dataRowList[0].dataCellList;

        for (let j = 0; j < dataCellList.length; j++) {
          if (j === 0) {
            tempDataObj.num = dataCellList[j].name;
          } else if (j === 1) {
            tempDataObj.description = dataCellList[j].name;
          } else if (j === 2) {
            tempDataObj.lineAttrKeyA = dataCellList[j].key.split('~')[1];
            tempDataObj.attrib_name_a = dataCellList[j].key.split('~')[0];
            tempDataObj.attrib_order_a = dataCellList[j].key.split('~')[2];
            tempDataObj.is_editable = dataCellList[j].property.is_editable_cell !== 'Y';
            tempDataObj.value1 = dataCellList[j].value ? parseInt(dataCellList[j].value) : 0;
          } else if (j === 3) {
            tempDataObj.lineAttrKeyB = dataCellList[j].key.split('~')[1];
            tempDataObj.attrib_name_b = dataCellList[j].key.split('~')[0];
            tempDataObj.attrib_order_b = dataCellList[j].key.split('~')[2];
            tempDataObj.is_editable = dataCellList[j].property.is_editable_cell !== 'Y';
            tempDataObj.value2 = dataCellList[j].value ? parseInt(dataCellList[j].value) : 0;
          }
        }

        tempDataObj.line_order = saveParamDtls[8];
        this.scheduleLFormView.rows.push(tempDataObj);
      }

      this.formListOriginal = _.cloneDeep(this.scheduleLFormView);
      this.computeValues();
      this.showFormLoader = false;
    }
    this.cdr.detectChanges();
  }

  fetchSchLimports() {
    const clientKey = this.USER_SETTINGS?.user?.client_key || this.globalService?.globalParams?.client_key;
    const params = {
      "client_key": clientKey,
      "process_name": this.filterParams?.process_name || "1120L Sch L",
      "status": 'Y'
    };
    
    console.log('[DEBUG] fetchSchLimports called with params:', params);

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=wwledr',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        console.log('[DEBUG] fetchSchLimports response:', data);
        if (data && data.jsonObject) {
          this.BatchList = data.jsonObject;
          this.BatchList.forEach((value, key) => {
            this.BatchList[key].TOGGLE = false;
          });
          console.log('[DEBUG] BatchList updated, new length:', this.BatchList.length);
        } else {
          console.warn('[DEBUG] No jsonObject in response');
        }
        this.showRequestLoader = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[DEBUG] Error fetching Schedule L imports:', error);
        this.showRequestLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadTransactions(index: number, trans_key: string) {
    this.batchLoader = true;
    const clientKey = this.USER_SETTINGS?.user?.client_key || this.globalService?.globalParams?.client_key;
    const params = {
      "client_key": clientKey,
      "process_name": this.filterParams?.process_name || "1120L Sch L",
      "transaction_key": trans_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=l6gzle',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          const tempData = data.jsonObject;
          let currLineNo: any;
          let tempBatchList: any = {};
          const tempBatchData: any[] = [];

          tempData.forEach((value: any, key: number) => {
            if (key === 0) {
              currLineNo = tempData[key].LINE_NO;
              tempBatchList = { ...tempData[key] };
              
              if ('BEGIN_A' === tempData[key].ATTRIBUTE_NAME) {
                tempBatchList.ATTRIBUTE_VALUE_A = tempData[key].ATTRIBUTE_VALUE;
              } else if ('END_B' === tempData[key].ATTRIBUTE_NAME) {
                tempBatchList.ATTRIBUTE_VALUE_B = tempData[key].ATTRIBUTE_VALUE;
              }
            } else {
              if (tempData[key].LINE_NO === currLineNo) {
                if ('BEGIN_A' === tempData[key].ATTRIBUTE_NAME) {
                  tempBatchList.ATTRIBUTE_VALUE_A = tempData[key].ATTRIBUTE_VALUE;
                } else if ('END_B' === tempData[key].ATTRIBUTE_NAME) {
                  tempBatchList.ATTRIBUTE_VALUE_B = tempData[key].ATTRIBUTE_VALUE;
                }
                tempBatchData.push({ ...tempBatchList });
                tempBatchList = {};
              } else {
                currLineNo = tempData[key].LINE_NO;
                tempBatchList = { ...tempData[key] };
                if ('BEGIN_A' === tempData[key].ATTRIBUTE_NAME) {
                  tempBatchList.ATTRIBUTE_VALUE_A = tempData[key].ATTRIBUTE_VALUE;
                } else if ('END_B' === tempData[key].ATTRIBUTE_NAME) {
                  tempBatchList.ATTRIBUTE_VALUE_B = tempData[key].ATTRIBUTE_VALUE;
                }
              }
            }
          });
          
          // Push last item if exists
          if (Object.keys(tempBatchList).length > 0) {
            tempBatchData.push({ ...tempBatchList });
          }

          this.BatchListData = tempBatchData;
        }
        this.batchLoader = false;
        this.showBatchData = true;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading transactions:', error);
        this.batchLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  showUploads(index: number, trans_key: string) {
    if (this.prev_index !== null) {
      if (this.prev_index !== index) {
        this.BatchList[this.prev_index].TOGGLE = false;
        this.prev_index = index;
        this.showBatchData = false;
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.loadTransactions(index, trans_key);
      } else {
        this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
        this.showBatchData = true;
      }
    } else {
      this.prev_index = index;
      this.BatchList[index].TOGGLE = !this.BatchList[index].TOGGLE;
      this.loadTransactions(index, trans_key);
    }
  }

  setDefault() {
    if (this.scheduleLFormView.rows.length > 8) {
      this.scheduleLFormView.rows[8].value1 = this.scheduleLFormView.rows[5].value1;
      this.scheduleLFormView.rows[8].value2 = this.scheduleLFormView.rows[5].value2;
    }
  }

  computeValues() {
    // Handle NaN issues
    for (let i = 0; i < Math.min(3, this.scheduleLFormView.rows.length); i++) {
      this.scheduleLFormView.rows[i].value1 = this.scheduleLFormView.rows[i].value1 || 0;
      this.scheduleLFormView.rows[i].value2 = this.scheduleLFormView.rows[i].value2 || 0;
    }
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const tempArray: any[] = [];

    for (let i = 0; i < this.scheduleLFormView.rows.length; i++) {
      const row = this.scheduleLFormView.rows[i];
      
      const formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKeyA,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.value1,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };

      const formRowDataB = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_b,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKeyB,
        Attribute_Name: row.attrib_name_b,
        Attribute_Value: row.value2,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };

      tempArray.push(formRowDataA);
      tempArray.push(formRowDataB);
    }

    this.formData.editrows.push(tempArray);
    const mainData: any[] = [];
    mainData.push(this.formData);

    // CRITICAL: Call service with proper parameters like F1120L-SCH-F
    this.f1120lSchLService.saveSchddForm(
      this.GENERAL_CONFIG.base_url, 
      mainData,
      this.globalService, 
      this.filterParams, 
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          // CRITICAL: Emit 'refresh' string for AngularJS modal close
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving form:', error);
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    this.scheduleLFormView = _.cloneDeep(this.formListOriginal);
    this.computeValues();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Update value methods for form inputs
  updateValue1(row: any, value: any) {
    row.value1 = value ? parseInt(value) : 0;
    this.computeValues();
  }

  updateValue2(row: any, value: any) {
    row.value2 = value ? parseInt(value) : 0;
    this.computeValues();
  }

  // Number formatting function
  numberWithCommas(x: any): string {
    if (x === null || x === undefined || x === '' || x === 'undefined') {
      return '';
    }
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }
}
